import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  AlertCircle, 
  CreditCard
} from 'lucide-react';

const DonationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [donationAmount, setDonationAmount] = useState('');
  const [donorDetails, setDonorDetails] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setDonorDetails(prev => ({
          ...prev,
          name: userData.entity?.troupe_name || prev.name,
          email: userData.email || prev.email
        }));
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => console.log('Razorpay SDK loaded successfully');
    script.onerror = () => setError('Failed to load payment gateway. Please refresh the page.');
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (field, value) => {
    setDonorDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const amount = parseInt(donationAmount);
    
    if (!amount || amount < 100) {
      return 'Please enter a donation amount of at least ₹100';
    }

    if (!donorDetails.name.trim()) {
      return 'Please enter your name';
    }

    if (!donorDetails.email.trim()) {
      return 'Please enter your email';
    }

    if (!/^\S+@\S+\.\S+$/.test(donorDetails.email)) {
      return 'Please enter a valid email address';
    }

    if (!donorDetails.phone.trim()) {
      return 'Please enter your phone number';
    }

    if (!/^\d{10}$/.test(donorDetails.phone)) {
      return 'Please enter a valid 10-digit phone number';
    }

    return null;
  };

  const handleDonation = async () => {
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const amount = parseInt(donationAmount) * 100;

    if (typeof window.Razorpay === 'undefined') {
      setError('Razorpay SDK not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);

    try {
      const options = {
        key: 'rzp_live_SPsWCHedFqqFDw',
        amount: amount,
        currency: 'INR',
        name: 'Tamil Nadu Scouts and Guides',
        description: 'Donation',
        handler: async function (response) {
          handlePaymentSuccess(response);
        },
        prefill: {
          name: donorDetails.name,
          email: donorDetails.email,
          contact: donorDetails.phone
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setError('Payment cancelled. Please try again.');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (err) {
      console.error('Payment Error:', err);
      setError('Failed to initiate payment. Please try again.');
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePaymentSuccess = (response) => {
    setLoading(false);
    setSuccess(`Thank you for your generous donation of ₹${donationAmount}!`);
    
    setDonationAmount('');
    setDonorDetails({
      name: '',
      email: '',
      phone: ''
    });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          <div className="text-center mb-8">
            <Heart className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
              Make a Donation
            </h2>
            <p className="text-gray-600">
              Support Tamil Nadu Scouts and Guides
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-2">
              <div className="h-5 w-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="h-2 w-2 bg-white rounded-full"></div>
              </div>
              <span className="text-green-700">{success}</span>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Donation Amount (₹) *
              </label>
              <input
                type="number"
                value={donationAmount}
                onChange={(e) => setDonationAmount(e.target.value)}
                placeholder="Enter amount"
                min="100"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">Minimum: ₹100</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={donorDetails.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={donorDetails.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={donorDetails.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="10-digit mobile number"
                pattern="[0-9]{10}"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleDonation}
              disabled={loading || !donationAmount}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:bg-blue-400 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5" />
                  Donate Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationPage;